/**
 * Request utility wrapper
 * Provides convenient methods for HTTP requests
 */
import { request as umiRequest } from '@umijs/max';

// Base response structure from backend
export interface Result<T = any> {
  Code: number;
  Message: string;
  Msg?: string;
  Data: T;
}

// Request options type
export interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, any>;
  [key: string]: any;
}

// API base URL prefix configuration (read from environment variable)
const API_PREFIX = process.env.UMI_APP_API_PREFIX || '/api';

/**
 * Get full URL with prefix
 * @param url - The relative URL path
 * @returns Full URL with prefix
 */
const getFullUrl = (url: string): string => {
  // If URL already starts with http(s) or the prefix, return as is
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith(API_PREFIX)) {
    return url;
  }
  // Ensure URL starts with /
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  return `${API_PREFIX}${normalizedUrl}`;
};

/**
 * Encapsulated request instance with convenient methods
 */
export const http = {
  /**
   * GET request
   */
  get<T = any>(url: string, options?: RequestOptions): Promise<Result<T>> {
    return umiRequest<Result<T>>(getFullUrl(url), {
      method: 'GET',
      ...options,
    });
  },

  /**
   * POST request
   */
  post<T = any>(url: string, data?: any, options?: RequestOptions): Promise<Result<T>> {
    return umiRequest<Result<T>>(getFullUrl(url), {
      method: 'POST',
      data,
      ...options,
    });
  },

  /**
   * PUT request
   */
  put<T = any>(url: string, data?: any, options?: RequestOptions): Promise<Result<T>> {
    return umiRequest<Result<T>>(getFullUrl(url), {
      method: 'PUT',
      data,
      ...options,
    });
  },

  /**
   * DELETE request
   */
  delete<T = any>(url: string, options?: RequestOptions): Promise<Result<T>> {
    return umiRequest<Result<T>>(getFullUrl(url), {
      method: 'DELETE',
      ...options,
    });
  },

  /**
   * PATCH request
   */
  patch<T = any>(url: string, data?: any, options?: RequestOptions): Promise<Result<T>> {
    return umiRequest<Result<T>>(getFullUrl(url), {
      method: 'PATCH',
      data,
      ...options,
    });
  },
};

// Also export the raw request for advanced usage
export { umiRequest as request };
